import InfoIcon from '@mui/icons-material/Info';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import {
  Button,
  Card,
  CardContent,
  Alert,
  Typography,
  Switch,
  Box,
  CircularProgress,
  FormControlLabel
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import notificationService from '../../services/notificationService';



/**
 * Bildirim aboneliği bileşeni
 * Kullanıcıların push notification'lara abone olmalarını sağlar
 */
interface NotificationSubscriberProps {
  onSubscriptionChange?: (isSubscribed: boolean) => void;
}

const NotificationSubscriber: React.FC<NotificationSubscriberProps> = ({ onSubscriptionChange }) => {
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSupported, setIsSupported] = useState<boolean>(true);
  const [message, setMessage] = useState<string>('');
  const [messageType, setMessageType] = useState<'success' | 'error' | 'info'>('info');

  // Bileşen yüklendiğinde abonelik durumunu kontrol et
  useEffect(() => {
    checkSubscriptionStatus();
  }, []);

  // Abonelik durumunu kontrol et
  const checkSubscriptionStatus = async () => {
    setIsLoading(true);
    try {
      // Tarayıcı desteğini kontrol et
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        setIsSupported(false);
        setIsLoading(false);
        return;
      }

      // Push Manager'ı başlat
      await notificationService.initializePushManager();
      
      // Abonelik durumunu kontrol et
      const subscribed = await notificationService.checkSubscriptionStatus();
      setIsSubscribed(subscribed);
      
      // Callback'i çağır
      if (onSubscriptionChange) {
        onSubscriptionChange(subscribed);
      }
    } catch (error) {
      console.error('Abonelik durumu kontrol edilirken hata oluştu:', error);
      setMessage('Abonelik durumu kontrol edilirken hata oluştu.');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  // Bildirimlere abone ol
  const handleSubscribe = async () => {
    setIsLoading(true);
    setMessage('');
    
    try {
      // Önce bildirim izni kontrolü
      if (Notification.permission === 'denied') {
        setMessage(
          'Bildirim izni daha önce reddedilmiş. Bildirimleri etkinleştirmek için tarayıcı ayarlarından izin vermeniz gerekiyor. ' +
          'Tarayıcı adres çubuğundaki kilit simgesine tıklayarak "Site Ayarları" bölümünden "Bildirimler" iznini "İzin Ver" olarak değiştirin.'
        );
        setMessageType('error');
        setIsLoading(false);
        return;
      }
      
      const result = await notificationService.subscribeToPushNotifications();
      if (result) {
        setIsSubscribed(true);
        setMessage('Bildirimlere başarıyla abone oldunuz.');
        setMessageType('success');
        
        // Callback'i çağır
        if (onSubscriptionChange) {
          onSubscriptionChange(true);
        }
      } else {
        // Bildirim izni kontrolü
        if (Notification.permission !== 'granted') {
          setMessage(
            'Bildirim izni verilmedi. Bildirimleri almak için tarayıcı izin isteminde "İzin Ver" seçeneğine tıklayın. ' +
            'Eğer izin istemi görünmediyse, tarayıcı adres çubuğundaki kilit simgesinden izinleri kontrol edin.'
          );
        } else {
          setMessage('Bildirimlere abone olunamadı. Lütfen tarayıcınızı yenileyin ve tekrar deneyin.');
        }
        setMessageType('error');
      }
    } catch (error) {
      console.error('Abone olunurken hata oluştu:', error);
      setMessage('Abone olunurken bir hata oluştu. Lütfen tarayıcınızı yenileyin ve tekrar deneyin.');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  // Bildirim aboneliğini iptal et
  const handleUnsubscribe = async () => {
    setIsLoading(true);
    setMessage('');
    
    try {
      const result = await notificationService.unsubscribeFromPushNotifications();
      if (result) {
        setIsSubscribed(false);
        setMessage('Bildirim aboneliğiniz iptal edildi.');
        setMessageType('info');
        
        // Callback'i çağır
        if (onSubscriptionChange) {
          onSubscriptionChange(false);
        }
      } else {
        setMessage('Bildirim aboneliği iptal edilemedi.');
        setMessageType('error');
      }
    } catch (error) {
      console.error('Abonelik iptal edilirken hata oluştu:', error);
      setMessage('Abonelik iptal edilirken hata oluştu.');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  // Test bildirimi gönder (sadece admin kullanıcıları için)
  const handleSendTestNotification = async () => {
    setIsLoading(true);
    setMessage('');
    
    try {
      const response = await notificationService.sendTestNotification();
      if (response.success) {
        setMessage('Test bildirimi gönderildi.');
        setMessageType('success');
      } else {
        setMessage(`Test bildirimi gönderilemedi: ${response.message}`);
        setMessageType('error');
      }
    } catch (error) {
      console.error('Test bildirimi gönderilirken hata oluştu:', error);
      setMessage('Test bildirimi gönderilirken hata oluştu.');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  // Bildirim durumunu değiştir (switch ile)
  const handleToggleSubscription = async (checked: boolean) => {
    if (checked) {
      await handleSubscribe();
    } else {
      await handleUnsubscribe();
    }
  };

  // Tarayıcı desteği yoksa
  if (!isSupported) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Bildirim Ayarları</Typography>
          <Alert
            severity="warning"
            sx={{ mt: 2 }}
          >
            Tarayıcınız push bildirimlerini desteklemiyor. Lütfen Chrome, Firefox, Edge veya Safari gibi modern bir tarayıcı kullanın.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>Bildirim Ayarları</Typography>
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ width: '100%' }}>
            {message && (
              <Alert 
                severity={messageType} 
                sx={{ mb: 2 }}
                onClose={() => setMessage('')}
              >
                {message}
              </Alert>
            )}
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" paragraph sx={{ display: 'flex', alignItems: 'center' }}>
                <InfoIcon sx={{ mr: 1 }} />
                Veritabanı yedekleme işlemleri tamamlandığında bildirim almak için push bildirimlerini etkinleştirin.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Not:</strong> Bildirimleri alabilmek için tarayıcınızın bildirim izinlerini kabul etmeniz gerekir.
                Bildirimleri, tarayıcınız kapalı olduğunda bile alabilirsiniz.
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isSubscribed}
                    onChange={(e) => handleToggleSubscription(e.target.checked)}
                    disabled={isLoading}
                    color="primary"
                  />
                }
                label={isSubscribed ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <NotificationsActiveIcon color="primary" sx={{ mr: 1 }} />
                    <Typography>Bildirimler Açık</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <NotificationsOffIcon sx={{ mr: 1 }} />
                    <Typography>Bildirimler Kapalı</Typography>
                  </Box>
                )}
              />
            </Box>
            
            {isSubscribed && (
              <Button 
                variant="outlined" 
                onClick={handleSendTestNotification} 
                disabled={isLoading}
                startIcon={<NotificationsIcon />}
              >
                Test Bildirimi Gönder
              </Button>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationSubscriber;
